import { Fragment, useCallback, useEffect, useState } from "react";
import { useStore } from "../context/StoreContext";
import { api, formatEUR, formatApiErrorDetail } from "../lib/api";
import Logo from "../components/Logo";
import { Monogram } from "../components/Logo";
import PasswordChanger from "../components/PasswordChanger";

const SIZES = ["XS", "S", "M", "L", "XL", "XXL", "3XL", "4XL"];
const STATUSES = [
  { key: "pending", label: "En attente" },
  { key: "paid", label: "Payée" },
  { key: "shipped", label: "Expédiée" },
  { key: "delivered", label: "Livrée" },
  { key: "cancelled", label: "Annulée" },
];

const emptyProduct = {
  name: "", description: "", price: "", category: "T-Shirts", colors: "Noir, Blanc",
  sizes: { XS: 5, S: 10, M: 15, L: 12, XL: 10, XXL: 8, "3XL": 5, "4XL": 4 }, images: [], featured: false, active: true,
};

const Admin = () => {
  const { adminToken, setAdminToken } = useStore();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [tab, setTab] = useState("apercu");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [messages, setMessages] = useState([]);
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(null);
  const [flash, setFlash] = useState("");
  const [uploading, setUploading] = useState(false);
  const [urlDraft, setUrlDraft] = useState("");
  const [openOrder, setOpenOrder] = useState(null);

  const toast = (msg) => { setFlash(msg); setTimeout(() => setFlash(""), 2600); };

  const refresh = useCallback(async () => {
    try {
      const [s, p, o, m, st] = await Promise.all([
        api.get("/admin/stats", adminToken),
        api.get("/admin/products", adminToken),
        api.get("/admin/orders", adminToken),
        api.get("/admin/messages", adminToken),
        api.get("/admin/settings", adminToken),
      ]);
      setStats(s); setProducts(p); setOrders(o); setMessages(m); setSettings(st);
    } catch (e) {
      if (String(e.message).includes("401")) { setAdminToken(""); }
    }
  }, [adminToken, setAdminToken]);

  useEffect(() => {
    if (!adminToken) return;
    refresh();
  }, [adminToken, refresh]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const data = await api.post("/admin/login", { email, password });
      setAdminToken(data.token);
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.message));
    }
  };

  const saveProduct = async (p) => {
    const colors = Array.isArray(p.colors)
      ? p.colors
      : String(p.colors || "").split(",").map((c) => c.trim()).filter(Boolean);
    const sizes = Array.isArray(p.sizes)
      ? p.sizes
      : Object.entries(p.sizes || {}).map(([size, stock]) => ({ size, stock: parseInt(stock, 10) || 0 }));
    const payload = {
      name: p.name, description: p.description, price: parseFloat(p.price),
      category: p.category, featured: !!p.featured, active: !!p.active,
      customizable: !!p.customizable, custom_max_len: 2,
      colors, images: Array.isArray(p.images) ? p.images : [],
      sizes,
    };
    try {
      if (p.id) {
        await api.put(`/admin/products/${p.id}`, payload, adminToken);
        toast("Produit mis à jour");
      } else {
        await api.post("/admin/products", payload, adminToken);
        toast("Produit créé");
      }
      setEditing(null); setCreating(null);
      refresh();
    } catch (err) {
      toast(formatApiErrorDetail(err.message));
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Supprimer définitivement ce produit ?")) return;
    await api.del(`/admin/products/${id}`, adminToken);
    toast("Produit supprimé");
    refresh();
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast("Photo trop lourde (5 Mo max)"); return; }
    setUploading(true);
    const data = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(file);
    });
    try {
      const res = await api.post("/admin/upload", { data }, adminToken);
      const target = creating || editing;
      (creating ? setCreating : setEditing)({ ...target, images: [...(target.images || []), res.url] });
      toast("Photo ajoutée");
    } catch (err) {
      toast(formatApiErrorDetail(err.message));
    }
    setUploading(false);
  };

  const removeImage = (idx) => {
    const target = creating || editing;
    (creating ? setCreating : setEditing)({ ...target, images: target.images.filter((_, i) => i !== idx) });
  };

  const addImageUrl = () => {
    const url = (urlDraft || "").trim();
    if (!url) return;
    const target = creating || editing;
    (creating ? setCreating : setEditing)({ ...target, images: [...(target.images || []), url] });
    setUrlDraft("");
  };

  if (!adminToken) {
    return (
      <main className="min-h-screen flex items-center justify-center px-4 py-24 grain" data-testid="admin-login-page">
        <div className="relative z-10 w-full max-w-md border border-line bg-coal p-8 sm:p-10">
          <div className="flex justify-center"><Monogram className="w-12 h-12 text-white" /></div>
          <h1 className="mt-6 text-center font-display font-extrabold uppercase tracking-widest text-2xl text-white">Espace Admin</h1>
          <p className="mt-2 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Moha66 · Accès réservé</p>
          <form onSubmit={login} className="mt-8 space-y-4" data-testid="admin-login-form">
            <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email admin"
              className="w-full bg-panel border border-line focus:border-white outline-none px-4 py-3 text-sm text-white placeholder:text-neutral-600" />
            <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mot de passe"
              className="w-full bg-panel border border-line focus:border-white outline-none px-4 py-3 text-sm text-white placeholder:text-neutral-600" />
            <button type="submit" data-testid="admin-login-submit" className="w-full bg-white text-ink font-mono text-xs uppercase tracking-[0.25em] py-4 hover:bg-gold transition-colors">
              Se connecter
            </button>
            {loginError && <p className="font-mono text-xs text-red-400 text-center" data-testid="admin-login-error">{loginError}</p>}
          </form>
        </div>
      </main>
    );
  }

  const inputCls = "w-full bg-panel border border-line focus:border-white outline-none px-3 py-2 text-sm text-white";

  return (
    <main className="min-h-screen pt-24 sm:pt-28 pb-24 px-4 sm:px-8 lg:px-16" data-testid="admin-dashboard">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-line pb-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-neutral-500">Moha66</p>
          <h1 className="mt-1 font-display font-extrabold uppercase tracking-tighter text-3xl sm:text-4xl text-white">Espace Admin</h1>
        </div>
        <div className="flex items-center gap-3">
          <PasswordChanger token={adminToken} onFlash={toast} />
          <button data-testid="admin-refresh-button" onClick={refresh} className="border border-line px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-300 hover:text-white hover:border-white transition-colors">
            Actualiser
          </button>
          <button data-testid="admin-logout-button" onClick={() => setAdminToken("")} className="border border-line px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-300 hover:text-white hover:border-white transition-colors">
            Déconnexion
          </button>
        </div>
      </div>

      <nav className="mt-6 flex flex-wrap gap-2" data-testid="admin-tabs">
        {[["apercu", "Aperçu"], ["produits", "Produits"], ["commandes", "Commandes"], ["reglages", "Réglages"], ["messages", "Messages"]].map(([k, label]) => (
          <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
            className={`px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] border transition-colors ${tab === k ? "bg-white text-ink border-white" : "border-line text-neutral-400 hover:text-white"}`}>
            {label}
          </button>
        ))}
      </nav>

      {flash && <p className="mt-4 border border-gold/40 text-gold px-4 py-3 font-mono text-xs uppercase tracking-[0.15em]" data-testid="admin-flash">{flash}</p>}

      {/* ---- APERÇU ---- */}
      {tab === "apercu" && stats && (
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="admin-stats-grid">
          {[
            { l: "Commandes", v: stats.orders_total },
            { l: "Payées", v: stats.orders_paid },
            { l: "En attente", v: stats.orders_pending },
            { l: "Chiffre d'affaires", v: formatEUR(stats.revenue) },
            { l: "Alertes stock (≤2)", v: stats.low_stock },
          ].map((s) => (
            <div key={s.l} className="border border-line bg-coal p-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{s.l}</p>
              <p className="mt-2 font-display font-extrabold text-2xl text-white" data-testid="admin-stat-value">{s.v}</p>
            </div>
          ))}
        </div>
      )}

      {/* ---- PRODUITS ---- */}
      {tab === "produits" && (
        <div className="mt-8" data-testid="admin-products-tab">
          <button
            data-testid="admin-new-product-button"
            onClick={() => setCreating({ ...emptyProduct, sizes: { ...emptyProduct.sizes } })}
            className="mb-6 bg-white text-ink font-mono text-[11px] uppercase tracking-[0.2em] px-6 py-3 hover:bg-gold transition-colors"
          >
            + Nouveau produit
          </button>

          {(creating || editing) && (
            <div className="mb-8 border border-lineActive bg-coal p-6" data-testid="admin-product-editor">
              <h3 className="font-display font-bold uppercase tracking-wider text-lg text-white">
                {creating ? "Créer un produit" : "Modifier le produit"}
              </h3>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input data-testid="admin-product-name-input" placeholder="Nom" value={(creating || editing).name} onChange={(e) => (creating ? setCreating : setEditing)({ ...(creating || editing), name: e.target.value })} className={inputCls} />
                <input data-testid="admin-product-price-input" type="number" step="0.01" min="0" placeholder="Prix €" value={(creating || editing).price} onChange={(e) => (creating ? setCreating : setEditing)({ ...(creating || editing), price: e.target.value })} className={inputCls} />
                <input data-testid="admin-product-category-input" placeholder="Catégorie" value={(creating || editing).category} onChange={(e) => (creating ? setCreating : setEditing)({ ...(creating || editing), category: e.target.value })} className={inputCls} />
                <input data-testid="admin-product-colors-input" placeholder="Coloris (séparés par des virgules)" value={Array.isArray((creating || editing).colors) ? (creating || editing).colors.join(", ") : ((creating || editing).colors || "")} onChange={(e) => (creating ? setCreating : setEditing)({ ...(creating || editing), colors: e.target.value })} className={inputCls} />
                <textarea data-testid="admin-product-description-input" placeholder="Description" rows="3" value={(creating || editing).description} onChange={(e) => (creating ? setCreating : setEditing)({ ...(creating || editing), description: e.target.value })} className={`${inputCls} sm:col-span-2`} />
                <div className="sm:col-span-2">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Photos de l'article</p>
                  <div className="flex flex-wrap gap-3 mb-3">
                    {((creating || editing).images || []).map((u, i) => (
                      <div key={i} className="relative w-20 h-24 border border-line overflow-hidden" data-testid={`admin-image-thumb-${i}`}>
                        <img src={u} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                        <button
                          data-testid={`admin-image-remove-${i}`}
                          onClick={() => removeImage(i)}
                          className="absolute top-0 right-0 bg-ink/85 text-white w-6 h-6 leading-none hover:bg-red-600 transition-colors"
                          aria-label="Retirer cette photo"
                        >×</button>
                      </div>
                    ))}
                    {uploading && (
                      <div className="w-20 h-24 border border-dashed border-lineActive flex items-center justify-center font-mono text-[10px] text-neutral-500">
                        Envoi…
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <label className="cursor-pointer bg-panel border border-line hover:border-white px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-neutral-300 text-center transition-colors">
                      + Téléverser une photo
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        data-testid="admin-image-upload-input"
                        onChange={(e) => { uploadPhoto(e.target.files[0]); e.target.value = ""; }}
                      />
                    </label>
                    <div className="flex flex-1 gap-2">
                      <input
                        data-testid="admin-image-url-input"
                        placeholder="…ou coller une URL d'image"
                        value={urlDraft}
                        onChange={(e) => setUrlDraft(e.target.value)}
                        className={inputCls}
                      />
                      <button
                        data-testid="admin-image-url-add"
                        onClick={addImageUrl}
                        className="border border-line px-4 font-mono text-[11px] uppercase tracking-[0.15em] text-neutral-300 hover:text-white hover:border-white transition-colors"
                      >
                        Ajouter
                      </button>
                    </div>
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Stock par taille</p>
                  <div className="grid grid-cols-5 gap-2">
                    {Object.entries((creating || editing).sizes).map(([s, v]) => (
                      <label key={s} className="text-center">
                        <span className="block font-mono text-[10px] text-neutral-500">{s}</span>
                        <input data-testid={`admin-product-stock-${s.toLowerCase()}`} type="number" min="0" value={v}
                          onChange={(e) => (creating ? setCreating : setEditing)({ ...(creating || editing), sizes: { ...(creating || editing).sizes, [s]: parseInt(e.target.value || "0", 10) } })}
                          className={`mt-1 w-full text-center ${inputCls}`} />
                      </label>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2 flex flex-wrap gap-6">
                  <label className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-neutral-300">
                    <input data-testid="admin-product-featured-toggle" type="checkbox" checked={!!(creating || editing).featured} onChange={(e) => (creating ? setCreating : setEditing)({ ...(creating || editing), featured: e.target.checked })} />
                    Mise en avant
                  </label>
                  <label className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-neutral-300">
                    <input data-testid="admin-product-customizable-toggle" type="checkbox" checked={!!(creating || editing).customizable} onChange={(e) => (creating ? setCreating : setEditing)({ ...(creating || editing), customizable: e.target.checked })} />
                    Personnalisable (n° 2 chiffres)
                  </label>
                  <label className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-neutral-300">
                    <input data-testid="admin-product-active-toggle" type="checkbox" checked={!!(creating || editing).active} onChange={(e) => (creating ? setCreating : setEditing)({ ...(creating || editing), active: e.target.checked })} />
                    Visible en boutique
                  </label>
                </div>
              </div>
              <div className="mt-5 flex gap-3">
                <button data-testid="admin-product-save-button" onClick={() => saveProduct(creating || editing)} className="bg-white text-ink font-mono text-[11px] uppercase tracking-[0.2em] px-8 py-3 hover:bg-gold transition-colors">Enregistrer</button>
                <button data-testid="admin-product-cancel-button" onClick={() => { setCreating(null); setEditing(null); }} className="border border-line font-mono text-[11px] uppercase tracking-[0.2em] px-8 py-3 text-neutral-300 hover:text-white">Annuler</button>
              </div>
            </div>
          )}

          <div className="space-y-3" data-testid="admin-products-list">
            {products.map((p) => (
              <div key={p.id} className="border border-line bg-coal" data-testid={`admin-product-row-${p.slug}`}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-5">
                  <div className="flex-1 min-w-0">
                    <p className="font-grotesk font-semibold uppercase text-sm text-white truncate">
                      {p.name} {p.featured && <span className="text-gold">★</span>} {!p.active && <span className="text-neutral-600">(masqué)</span>}
                    </p>
                    <p className="mt-1 font-mono text-[11px] text-neutral-500">
                      {formatEUR(p.price)} · {p.category} · Stock : {p.sizes.map((s) => `${s.size}:${s.stock}`).join(" ")}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button data-testid={`admin-product-edit-${p.slug}`} onClick={() => setEditing({ ...p, sizes: Object.fromEntries(p.sizes.map((s) => [s.size, s.stock])), images: p.images || [] })} className="border border-line px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-300 hover:text-white hover:border-white">
                      Modifier
                    </button>
                    <button data-testid={`admin-product-delete-${p.slug}`} onClick={() => deleteProduct(p.id)} className="border border-line px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-red-400 hover:border-red-400">
                      Supprimer
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---- COMMANDES ---- */}
      {tab === "commandes" && (
        <div className="mt-8 overflow-x-auto" data-testid="admin-orders-tab">
          {orders.length === 0 ? (
            <p className="py-20 text-center font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">Aucune commande pour le moment</p>
          ) : (
            <table className="w-full border border-line text-left" data-testid="admin-orders-table">
              <thead>
                <tr className="bg-coal border-b border-line">
                  {["N°", "Client", "Pièces", "Total", "Paiement", "Statut", ""].map((h, i) => (
                    <th key={i} className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <Fragment key={o.id}>
                    <tr className="border-b border-line last:border-0" data-testid={`admin-order-row-${o.order_number}`}>
                      <td className="px-4 py-3 font-mono text-xs text-white">{o.order_number}</td>
                      <td className="px-4 py-3 text-xs text-neutral-300">
                        {o.customer.name}<br />
                        <span className="text-neutral-500">{o.customer.email}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-neutral-400">
                        {o.items.map((it) => `${it.name} (${it.size}) ×${it.quantity}`).join(", ")}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-silver">{formatEUR(o.total)}</td>
                      <td className="px-4 py-3">
                        <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border ${o.payment_status === "paid" ? "border-green-500/40 text-green-400" : "border-line text-neutral-500"}`} data-testid={`admin-order-payment-${o.order_number}`}>
                          {o.payment_status === "paid" ? "Payé" : "Non payé"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <select
                          data-testid={`admin-order-status-select-${o.order_number}`}
                          value={o.status}
                          onChange={async (e) => { await api.put(`/admin/orders/${o.id}`, { status: e.target.value }, adminToken); toast("Statut mis à jour"); refresh(); }}
                          className="bg-ink border border-line text-xs text-white px-2 py-1.5 outline-none"
                        >
                          {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          data-testid={`admin-order-details-toggle-${o.order_number}`}
                          onClick={() => setOpenOrder(openOrder === o.id ? null : o.id)}
                          className="font-mono text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border border-line text-neutral-400 hover:text-white hover:border-white transition-colors"
                        >
                          {openOrder === o.id ? "Fermer" : "Détails"}
                        </button>
                      </td>
                    </tr>
                    {openOrder === o.id && (
                      <tr className="bg-black/40">
                        <td colSpan={7} className="px-4 py-5" data-testid={`admin-order-details-${o.order_number}`}>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 text-xs text-neutral-300">
                            <div>
                              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Client</p>
                              <p className="mt-2 text-white">{o.customer.name}</p>
                              <p className="text-neutral-500">{o.customer.email}</p>
                              <p className="mt-1 font-mono" data-testid={`admin-order-phone-${o.order_number}`}>{o.customer.phone || "—"}</p>
                            </div>
                            <div>
                              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Livraison</p>
                              {o.customer.delivery_method === "relais" ? (
                                <p className="mt-2 text-white font-semibold uppercase" data-testid={`admin-order-relais-${o.order_number}`}>
                                  Point relais Mondial Relay — {o.customer.relais_name}
                                </p>
                              ) : (
                                <p className="mt-2">À domicile</p>
                              )}
                              <p className="mt-1">{o.customer.address}<br />{o.customer.postal_code} {o.customer.city} — {o.customer.country}</p>
                            </div>
                            <div>
                              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Montants</p>
                              <p className="mt-2">Sous-total : <span className="font-mono text-white">{formatEUR(o.subtotal)}</span></p>
                              <p>Livraison : <span className="font-mono text-white">{formatEUR(o.shipping_fee)}</span></p>
                              <p className="mt-2 text-neutral-500">
                                Commandée le {new Date(o.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ---- RÉGLAGES ---- */}
      {tab === "reglages" && settings && (
        <div className="mt-8 max-w-xl border border-line bg-coal p-6 sm:p-8" data-testid="admin-settings-tab">
          <h3 className="font-display font-bold uppercase tracking-wider text-lg text-white">Frais de livraison</h3>
          <p className="mt-2 text-xs text-neutral-500">Montants en euros, appliqués au checkout selon le pays de livraison.</p>
          <div className="mt-6 grid grid-cols-2 gap-4">
            {[["france", "France"], ["europe", "Europe"], ["international", "International"], ["free_threshold", "Livraison offerte dès"]].map(([k, label]) => (
              <label key={k}>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{label} (€)</span>
                <input
                  data-testid={`admin-settings-${k}-input`}
                  type="number" step="0.01" min="0" value={settings.shipping[k]}
                  onChange={(e) => setSettings({ ...settings, shipping: { ...settings.shipping, [k]: parseFloat(e.target.value || "0") } })}
                  className={`mt-2 ${inputCls}`}
                />
              </label>
            ))}
          </div>
          <button
            data-testid="admin-settings-save-button"
            onClick={async () => { await api.put("/admin/settings", settings.shipping, adminToken); toast("Frais de livraison mis à jour"); refresh(); }}
            className="mt-6 bg-white text-ink font-mono text-[11px] uppercase tracking-[0.2em] px-8 py-3 hover:bg-gold transition-colors"
          >
            Enregistrer
          </button>
        </div>
      )}

      {/* ---- MESSAGES ---- */}
      {tab === "messages" && (
        <div className="mt-8 space-y-3" data-testid="admin-messages-tab">
          {messages.length === 0 ? (
            <p className="py-20 text-center font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">Aucun message</p>
          ) : messages.map((m) => (
            <div key={m.id} className={`border p-5 ${m.read ? "border-line bg-coal" : "border-lineActive bg-panel"}`} data-testid="admin-message-item">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-white font-semibold">{m.subject || "(sans objet)"} — <span className="text-neutral-400 font-normal">{m.name}</span></p>
                  <p className="font-mono text-[11px] text-neutral-500">{m.email} · {new Date(m.created_at).toLocaleString("fr-FR")}</p>
                </div>
                {!m.read && (
                  <button data-testid={`admin-message-read-${m.id}`} onClick={async () => { await api.put(`/admin/messages/${m.id}/read`, {}, adminToken); refresh(); }} className="border border-line px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-300 hover:text-white">
                    Marquer lu
                  </button>
                )}
              </div>
              <p className="mt-3 text-sm text-neutral-300 leading-relaxed">{m.message}</p>
            </div>
          ))}
        </div>
      )}
    </main>
  );
};

export default Admin;
